import React, { useState, useEffect } from 'react';
import { X, Copy, Check, QrCode, RefreshCw } from 'lucide-react';
import toast from 'react-hot-toast';
import { QRCodeSVG } from 'qrcode.react';
import { 
  getTrc20Data, 
  getErc20Data, 
  getPaymentConfig,
  createDonationOrder,
  confirmDonationOrder,
  createRoomPackageOrder,
  confirmRoomPackageOrder,
  createRoomCreationOrder,
  confirmRoomCreationOrder
} from '../services/api';
import type { NetworkType, PaymentConfig } from '../types';

export type PaymentType = 'donation' | 'room_package' | 'room_creation';

interface PaymentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  defaultNetwork?: NetworkType;
  paymentType: PaymentType;
  donationAmount?: string;
  selectedPeriod?: 'monthly' | 'quarterly' | 'yearly';
  roomName?: string;
  roomDescription?: string;
}

export const PaymentModal: React.FC<PaymentModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  defaultNetwork = 'trc20',
  paymentType,
  donationAmount: initialAmount = '',
  selectedPeriod: initialPeriod = 'monthly',
  roomName = '',
  roomDescription = ''
}) => {
  const [donationAmount, setDonationAmount] = useState(initialAmount);
  const [isAnonymous, setIsAnonymous] = useState(false);
  const [networkType, setNetworkType] = useState<NetworkType>(defaultNetwork);
  const [step, setStep] = useState<'input' | 'waiting' | 'success'>('input');
  const [orderData, setOrderData] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [copiedAddress, setCopiedAddress] = useState(false);
  const [copiedNote, setCopiedNote] = useState(false);
  const [selectedPeriod, setSelectedPeriod] = useState<'monthly' | 'quarterly' | 'yearly'>(initialPeriod);
  const [showQr, setShowQr] = useState(false);
  const [checkingPayment, setCheckingPayment] = useState(false);
  const [paymentCheckInterval, setPaymentCheckInterval] = useState<NodeJS.Timeout | null>(null);
  const [config, setConfig] = useState<PaymentConfig | null>(null);
  const [configLoading, setConfigLoading] = useState(true);
  const [packagePrice, setPackagePrice] = useState(50);

  useEffect(() => {
    if (isOpen) {
      loadConfig();
    }
  }, [isOpen]);

  const loadConfig = async () => {
    setConfigLoading(true);
    try {
      const result = await getPaymentConfig();
      if (result.success) {
        setConfig(result.data);
      }
    } catch (error) {
      console.error('加载配置失败:', error);
    } finally {
      setConfigLoading(false);
    }
  };

  useEffect(() => {
    if (config?.roomPackage) {
      switch (selectedPeriod) {
        case 'quarterly':
          setPackagePrice(config.roomPackage.quarterlyPrice || 35);
          break;
        case 'yearly':
          setPackagePrice(config.roomPackage.yearlyPrice || 100);
          break;
        default:
          setPackagePrice(config.roomPackage.monthlyPrice || 15);
      }
    }
  }, [selectedPeriod, config]);

  const resetState = () => {
    setDonationAmount(initialAmount);
    setIsAnonymous(false);
    setNetworkType(defaultNetwork);
    setStep('input');
    setOrderData(null);
    setLoading(false);
    setCopiedAddress(false);
    setCopiedNote(false);
    setShowQr(false);
    if (paymentCheckInterval) {
      clearInterval(paymentCheckInterval);
      setPaymentCheckInterval(null);
    }
  };

  const handleClose = () => {
    resetState();
    onClose();
  };

  const createOrder = async () => {
    if (!config) {
      toast.error('配置加载中，请稍候');
      return;
    }
    
    let amount: number;
    if (paymentType === 'room_package') {
      amount = packagePrice;
    } else if (paymentType === 'room_creation') {
      switch (selectedPeriod) {
        case 'quarterly':
          amount = config.roomPackage?.quarterlyPrice || 35;
          break;
        case 'yearly':
          amount = config.roomPackage?.yearlyPrice || 100;
          break;
        default:
          amount = config.roomPackage?.monthlyPrice || 15;
      }
    } else {
      amount = parseFloat(donationAmount);
    }
    
    if (!amount || amount < 1) {
      toast.error('请输入有效的金额（最少1USDT）');
      return;
    }

    if (paymentType === 'room_creation' && !roomName) {
      toast.error('请输入房间名称');
      return;
    }

    try {
      setLoading(true);
      let result;
      
      if (paymentType === 'room_package') {
        result = await createRoomPackageOrder({
          network: networkType,
          period: selectedPeriod
        });
      } else if (paymentType === 'room_creation') {
        result = await createRoomCreationOrder({
          room_name: roomName,
          room_description: roomDescription,
          network: networkType,
          period: selectedPeriod
        });
      } else {
        result = await createDonationOrder({
          amount,
          isAnonymous,
          network: networkType
        });
      }

      if (result.success && result.data) {
        setOrderData(result.data);
        setStep('waiting');
        startPaymentMonitoring();
      } else {
        toast.error(result.message || '创建订单失败');
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || '创建订单失败');
    } finally {
      setLoading(false);
    }
  };

  const copyAddress = async () => {
    if (!orderData) return;
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(orderData.paymentAddress);
        setCopiedAddress(true);
        toast.success('地址已复制');
        setTimeout(() => setCopiedAddress(false), 2000);
      } else {
        const textArea = document.createElement('textarea');
        textArea.value = orderData.paymentAddress;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        textArea.style.top = '-999999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        const successful = document.execCommand('copy');
        document.body.removeChild(textArea);
        if (successful) {
          setCopiedAddress(true);
          toast.success('地址已复制');
          setTimeout(() => setCopiedAddress(false), 2000);
        } else {
          toast.error('复制失败，请手动复制');
        }
      }
    } catch (error) {
      toast.error('复制失败，请手动复制');
    }
  };

  const copyNote = async () => {
    if (!orderData) return;
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(orderData.systemNote);
        setCopiedNote(true);
        toast.success('备注已复制');
        setTimeout(() => setCopiedNote(false), 2000);
      } else {
        const textArea = document.createElement('textarea');
        textArea.value = orderData.systemNote;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        textArea.style.top = '-999999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        const successful = document.execCommand('copy');
        document.body.removeChild(textArea);
        if (successful) {
          setCopiedNote(true);
          toast.success('备注已复制');
          setTimeout(() => setCopiedNote(false), 2000);
        } else {
          toast.error('复制失败，请手动复制');
        }
      }
    } catch (error) {
      toast.error('复制失败，请手动复制');
    }
  };

  const checkUsdtTransaction = async (): Promise<{ found: boolean; txid?: string; fromAddress?: string }> => {
    if (!orderData) return { found: false };

    try {
      const address = orderData.paymentAddress.toLowerCase();
      const expectedAmount = orderData.amount.toString();
      const expectedMemo = orderData.systemNote;
      
      // 区块确认数要求（超过2个区块确认才算确认）
      const minConfirmations = 2;

      if (networkType === 'trc20') {
        const result = await getTrc20Data();
        
        // 如果是mock数据，跳过验证（避免误匹配）
        if (result.success && result.data && !result.data.isMockData && result.data.transactions) {
          const currentTime = Date.now();
          const matchingTx = result.data.transactions.find((tx: any) => {
            if (tx.type !== 'in') return false;
            
            // 验证收款地址
            const txAddress = (tx.to || '').toLowerCase();
            if (txAddress !== address) return false;

            // 验证订单金额
            const txAmount = parseFloat(tx.value);
            const expected = parseFloat(expectedAmount);
            if (Math.abs(txAmount - expected) >= 0.000001) return false;

            // 验证系统备注（memo）
            const txMemo = tx.memo || '';
            if (txMemo !== expectedMemo) {
              console.log('Memo mismatch:', txMemo, 'vs', expectedMemo);
              return false;
            }

            // 验证区块确认（通过时间判断，TRC20约3秒出块，2个确认约6秒以上）
            const blockTime = tx.timestamp * 1000;
            if (!blockTime) return false;
            const confirmTime = minConfirmations * 3000; // 每个区块约3秒，2个确认约6秒
            if (currentTime - blockTime < confirmTime) {
              console.log('Insufficient confirmations:', currentTime - blockTime, '<', confirmTime);
              return false;
            }

            return true;
          });

          if (matchingTx) {
            console.log('Payment verified:', matchingTx.txhash);
            return {
              found: true,
              txid: matchingTx.txhash,
              fromAddress: matchingTx.from
            };
          }
        }
      } else {
        const result = await getErc20Data();
        
        // 如果是mock数据，跳过验证（避免误匹配）
        if (result.success && result.data && !result.data.isMockData && result.data.transactions) {
          const currentTime = Date.now();
          const matchingTx = result.data.transactions.find((tx: any) => {
            if (tx.type !== 'in') return false;
            
            // 验证收款地址
            const txAddress = (tx.to || '').toLowerCase();
            if (txAddress !== address) return false;

            // 验证订单金额
            const txAmount = parseFloat(tx.value);
            const expected = parseFloat(expectedAmount);
            if (Math.abs(txAmount - expected) >= 0.000001) return false;

            // 验证系统备注（ERC20通过tx.input或特殊字段传递，这里检查是否有memo）
            const txMemo = tx.memo || tx.input || '';
            if (expectedMemo && !txMemo.includes(expectedMemo)) {
              console.log('Memo mismatch:', txMemo, 'vs', expectedMemo);
              return false;
            }

            // 验证区块确认（通过时间判断，ETH约15秒出块，2个确认约30秒以上）
            const blockTime = tx.timestamp * 1000;
            if (!blockTime) return false;
            const confirmTime = minConfirmations * 15000; // 每个区块约15秒，2个确认约30秒
            if (currentTime - blockTime < confirmTime) {
              console.log('Insufficient confirmations:', currentTime - blockTime, '<', confirmTime);
              return false;
            }

            return true;
          });

          if (matchingTx) {
            console.log('Payment verified:', matchingTx.txhash);
            return {
              found: true,
              txid: matchingTx.txhash,
              fromAddress: matchingTx.from
            };
          }
        }
      }

      return { found: false };
    } catch (error) {
      console.error('查询USDT交易失败:', error);
      return { found: false };
    }
  };

  const startPaymentMonitoring = () => {
    if (paymentCheckInterval) return;

    toast.success('开始监控USDT支付，请在30分钟内完成支付');

    const interval = setInterval(async () => {
      const result = await checkUsdtTransaction();
      if (result.found && result.txid) {
        clearInterval(interval);
        setPaymentCheckInterval(null);
        toast.success('检测到支付，准备确认订单...');

        try {
          let confirmResult;
          
          if (paymentType === 'room_package') {
            confirmResult = await confirmRoomPackageOrder({
              orderId: orderData.orderId,
              txid: result.txid,
              fromAddress: result.fromAddress
            });
          } else if (paymentType === 'room_creation') {
            confirmResult = await confirmRoomCreationOrder({
              orderId: orderData.orderId,
              txid: result.txid,
              fromAddress: result.fromAddress
            });
          } else {
            confirmResult = await confirmDonationOrder({
              orderId: orderData.orderId,
              txid: result.txid,
              fromAddress: result.fromAddress
            });
          }

          if (confirmResult.success) {
            let successMsg;
            if (paymentType === 'room_package') {
              successMsg = '购买成功！房间创建权限已开通';
            } else if (paymentType === 'room_creation') {
              successMsg = '创建成功！房间已开通';
            } else {
              successMsg = '捐助成功！感谢您的支持';
            }
            toast.success(successMsg);
            setStep('success');
            setTimeout(() => {
              if (onSuccess) onSuccess();
              handleClose();
            }, 2000);
          } else {
            toast.error(confirmResult.message || '确认订单失败');
          }
        } catch (error: any) {
          toast.error(error.response?.data?.message || '确认订单失败');
        }
      }
    }, 10000);

    setPaymentCheckInterval(interval);

    setTimeout(() => {
      if (interval) {
        clearInterval(interval);
        setPaymentCheckInterval(null);
        if (orderData) {
          toast.error('支付超时，请重新下单');
          handleClose();
        }
      }
    }, 30 * 60 * 1000);
  };

  const handleManualCheck = async () => {
    setCheckingPayment(true);
    try {
      const result = await checkUsdtTransaction();
      if (result.found && result.txid) {
        toast.success('检测到支付，准备确认订单...');

        let confirmResult;
        
        if (paymentType === 'room_package') {
          confirmResult = await confirmRoomPackageOrder({
            orderId: orderData.orderId,
            txid: result.txid,
            fromAddress: result.fromAddress
          });
        } else if (paymentType === 'room_creation') {
          confirmResult = await confirmRoomCreationOrder({
            orderId: orderData.orderId,
            txid: result.txid,
            fromAddress: result.fromAddress
          });
        } else {
          confirmResult = await confirmDonationOrder({
            orderId: orderData.orderId,
            txid: result.txid,
            fromAddress: result.fromAddress
          });
        }

        if (confirmResult.success) {
          let successMsg;
          if (paymentType === 'room_package') {
            successMsg = '购买成功！房间创建权限已开通';
          } else if (paymentType === 'room_creation') {
            successMsg = '创建成功！房间已开通';
          } else {
            successMsg = '捐助成功！感谢您的支持';
          }
          toast.success(successMsg);
          setStep('success');
          setTimeout(() => {
            if (onSuccess) onSuccess();
            handleClose();
          }, 2000);
        } else {
          toast.error(confirmResult.message || '确认订单失败');
        }
      } else {
        toast.error('未检测到支付，请确认已转账');
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || '检查失败');
    } finally {
      setCheckingPayment(false);
    }
  };

  if (!isOpen) return null;

  const getTitle = () => {
    if (paymentType === 'room_package') {
      return '🏠 购买房间主人套餐';
    } else if (paymentType === 'room_creation') {
      return '🏠 创建房间';
    }
    return '🧡 自愿捐助';
  };

  const getDescription = () => {
    if (paymentType === 'room_package') {
      return '开通房间创建权限，创建您的付费会员房间';
    } else if (paymentType === 'room_creation') {
      return '支付创建房间的费用，创建您的专属房间';
    }
    return '感谢您对社区的支持！捐助仅代表您对社区的支持，不提供任何特权。';
  };

  const getAmountLabel = () => {
    if (paymentType === 'room_package') {
      return `套餐价格 (USDT) - ${packagePrice} USDT`;
    } else if (paymentType === 'room_creation') {
      const roomCreationPrice = config?.roomPackage?.monthlyPrice || packagePrice;
      return `创建费用 (USDT) - ${roomCreationPrice} USDT`;
    }
    return '捐助金额 (USDT)';
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <h2 className="text-xl font-bold text-gray-800">{getTitle()}</h2>
          <button
            onClick={handleClose}
            className="p-1 text-gray-500 hover:text-gray-800 rounded hover:bg-gray-100"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-4">
          {step === 'input' && (
            <div>
              <div className="text-sm text-gray-500 mb-4">
                {getDescription()}
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  选择网络
                </label>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setNetworkType('trc20')}
                    className={`flex-1 px-4 py-2 rounded-lg border ${
                      networkType === 'trc20'
                        ? 'border-pink-500 bg-pink-50 text-pink-700'
                        : 'border-gray-300 text-gray-700 hover:border-gray-400'
                    }`}
                  >
                    TRC20 (TRON)
                  </button>
                  <button
                    type="button"
                    onClick={() => setNetworkType('erc20')}
                    className={`flex-1 px-4 py-2 rounded-lg border ${
                      networkType === 'erc20'
                        ? 'border-purple-500 bg-purple-50 text-purple-700'
                        : 'border-gray-300 text-gray-700 hover:border-gray-400'
                    }`}
                  >
                    ERC20 (ETH)
                  </button>
                </div>
              </div>

              {paymentType === 'room_package' && (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    选择套餐周期
                  </label>
                  <div className="grid grid-cols-3 gap-2">
                    <button
                      type="button"
                      onClick={() => setSelectedPeriod('monthly')}
                      className={`px-3 py-2 rounded-lg border text-sm ${
                        selectedPeriod === 'monthly'
                          ? 'border-pink-500 bg-pink-50 text-pink-700'
                          : 'border-gray-300 text-gray-700 hover:border-gray-400'
                      }`}
                    >
                      月费<br />{config?.roomPackage?.monthlyPrice || 15} USDT
                    </button>
                    <button
                      type="button"
                      onClick={() => setSelectedPeriod('quarterly')}
                      className={`px-3 py-2 rounded-lg border text-sm ${
                        selectedPeriod === 'quarterly'
                          ? 'border-pink-500 bg-pink-50 text-pink-700'
                          : 'border-gray-300 text-gray-700 hover:border-gray-400'
                      }`}
                    >
                      季费<br />{config?.roomPackage?.quarterlyPrice || 35} USDT
                    </button>
                    <button
                      type="button"
                      onClick={() => setSelectedPeriod('yearly')}
                      className={`px-3 py-2 rounded-lg border text-sm ${
                        selectedPeriod === 'yearly'
                          ? 'border-pink-500 bg-pink-50 text-pink-700'
                          : 'border-gray-300 text-gray-700 hover:border-gray-400'
                      }`}
                    >
                      年费<br />{config?.roomPackage?.yearlyPrice || 100} USDT
                    </button>
                  </div>
                </div>
              )}

              {paymentType === 'donation' && (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {getAmountLabel()}
                  </label>
                  <input
                    type="number"
                    value={donationAmount}
                    onChange={(e) => setDonationAmount(e.target.value)}
                    min="1"
                    step="0.000001"
                    placeholder="请输入捐助金额（最少1USDT）"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500"
                  />
                </div>
              )}

              {paymentType === 'room_package' && (
                <div className="mb-4 bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-700">房间主人套餐</span>
                    <span className="text-2xl font-bold text-purple-600">{packagePrice} USDT</span>
                  </div>
                  <p className="text-sm text-gray-600 mt-2">
                    {selectedPeriod === 'monthly' && '有效期1个月'}
                    {selectedPeriod === 'quarterly' && '有效期3个月'}
                    {selectedPeriod === 'yearly' && '有效期1年'}
                  </p>
                </div>
              )}

              {paymentType === 'room_creation' && (
                <div className="mb-4 space-y-3">
                  <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg p-4">
                    <div className="flex items-center justify-between">
                      <span className="text-gray-700">房间创建</span>
                      <span className="text-2xl font-bold text-blue-600">{packagePrice} USDT</span>
                    </div>
                    <p className="text-sm text-gray-600 mt-2">
                      {selectedPeriod === 'monthly' && '有效期1个月'}
                      {selectedPeriod === 'quarterly' && '有效期3个月'}
                      {selectedPeriod === 'yearly' && '有效期1年'}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setSelectedPeriod('monthly')}
                      className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-colors ${
                        selectedPeriod === 'monthly'
                          ? 'bg-blue-500 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      月度
                    </button>
                    <button
                      onClick={() => setSelectedPeriod('quarterly')}
                      className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-colors ${
                        selectedPeriod === 'quarterly'
                          ? 'bg-blue-500 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      季度
                    </button>
                    <button
                      onClick={() => setSelectedPeriod('yearly')}
                      className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-colors ${
                        selectedPeriod === 'yearly'
                          ? 'bg-blue-500 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      年度
                    </button>
                  </div>
                  {roomName && (
                    <div className="bg-gray-50 rounded-lg p-3">
                      <div className="text-sm text-gray-600 mb-1">房间名称</div>
                      <div className="text-gray-900 font-medium">{roomName}</div>
                    </div>
                  )}
                  {roomDescription && (
                    <div className="bg-gray-50 rounded-lg p-3">
                      <div className="text-sm text-gray-600 mb-1">房间描述</div>
                      <div className="text-gray-900">{roomDescription}</div>
                    </div>
                  )}
                </div>
              )}

              {paymentType === 'donation' && (
                <div className="mb-4">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={isAnonymous}
                      onChange={(e) => setIsAnonymous(e.target.checked)}
                      className="w-4 h-4 text-pink-500 rounded focus:ring-pink-500"
                    />
                    <span className="ml-2 text-sm text-gray-700">匿名捐助（不显示在荣誉榜）</span>
                  </label>
                </div>
              )}

              <button
                onClick={createOrder}
                disabled={loading || configLoading || (paymentType === 'donation' && (!donationAmount || parseFloat(donationAmount) < 1))}
                className="w-full py-3 bg-gradient-to-r from-pink-500 to-purple-500 text-white rounded-lg font-medium hover:from-pink-600 hover:to-purple-600 transition-colors disabled:opacity-50"
              >
                {configLoading ? '加载配置中...' : loading ? '创建订单中...' : '生成订单'}
              </button>
            </div>
          )}

          {step === 'waiting' && orderData && (
            <div className="space-y-4">
              <div className="text-center mb-4">
                <div className="text-gray-600 mb-2">请完成以下转账</div>
                <div className="text-3xl font-bold text-pink-600">
                  {orderData.amount.toFixed(6)} USDT
                </div>
              </div>

              <div className="border border-gray-200 rounded-lg p-3">
                <div className="text-sm text-gray-600 mb-2">收款地址（{networkType === 'trc20' ? 'TRC20' : 'ERC20'}）</div>
                <div className="bg-gray-100 rounded p-2 text-sm break-all mb-2">
                  {orderData.paymentAddress}
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={copyAddress}
                    className="flex-1 flex items-center justify-center gap-1 px-3 py-2 bg-pink-500 text-white rounded hover:bg-pink-600 transition-colors text-sm"
                  >
                    {copiedAddress ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                    {copiedAddress ? '已复制' : '复制地址'}
                  </button>
                  <button
                    onClick={() => setShowQr(!showQr)}
                    className="flex items-center justify-center gap-1 px-3 py-2 bg-purple-500 text-white rounded hover:bg-purple-600 transition-colors text-sm"
                  >
                    <QrCode className="w-4 h-4" />
                    {showQr ? '隐藏' : '二维码'}
                  </button>
                </div>
              </div>

              {showQr && (
                <div className="flex justify-center">
                  <div className="bg-white p-4 border border-pink-200 rounded-lg">
                    <QRCodeSVG value={orderData.paymentAddress} size={192} level="H" />
                  </div>
                </div>
              )}

              <div className="border border-gray-200 rounded-lg p-3">
                <div className="text-sm text-gray-600 mb-2">系统备注（必填）</div>
                <div className="bg-yellow-50 border border-yellow-200 rounded p-2 text-sm break-all mb-2">
                  {orderData.systemNote}
                </div>
                <button
                  onClick={copyNote}
                  className="flex items-center justify-center gap-1 px-3 py-2 bg-yellow-500 text-white rounded hover:bg-yellow-600 transition-colors text-sm"
                >
                  {copiedNote ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  {copiedNote ? '已复制' : '复制备注'}
                </button>
              </div>

              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                <div className="text-sm font-medium text-yellow-800 mb-2">
                  ⚠️ 重要提示
                </div>
                <ul className="text-xs text-yellow-800 space-y-1">
                  <li>• 请使用 {networkType === 'trc20' ? 'TRC20' : 'ERC20'} 网络，选错网络资产无法找回</li>
                  <li>• 转账金额必须与显示完全一致</li>
                  <li>• 必须复制系统生成备注并填写在备注中</li>
                  <li>• 链上确认后会自动同步订单状态</li>
                </ul>
              </div>

              <div className="flex gap-2">
                <button
                  onClick={handleManualCheck}
                  disabled={checkingPayment}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors disabled:opacity-50"
                >
                  <RefreshCw className={`w-4 h-4 ${checkingPayment ? 'animate-spin' : ''}`} />
                  {checkingPayment ? '检查中...' : '手动检查'}
                </button>
                <button
                  onClick={handleClose}
                  className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
                >
                  关闭
                </button>
              </div>

              <div className="text-center text-sm text-gray-500">
                <div className="flex items-center justify-center gap-2">
                  <div className="w-2 h-2 bg-pink-500 rounded-full animate-pulse"></div>
                  自动监控中，每10秒检查一次
                </div>
              </div>
            </div>
          )}

          {step === 'success' && (
            <div className="text-center py-8">
              <div className="text-6xl mb-4">🎉</div>
              <div className="text-2xl font-bold text-green-600 mb-2">
                {paymentType === 'room_package' ? '购买成功！' : paymentType === 'room_creation' ? '创建成功！' : '捐助成功！'}
              </div>
              <div className="text-gray-600">
                {paymentType === 'room_package' ? '房间创建权限已开通！' : paymentType === 'room_creation' ? '房间已创建！' : '感谢您的支持！'}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PaymentModal;